import { ICoordinate, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'
import { formatNumberCol } from '@makyoapp/makyo-common'

import { Cell, ICell } from '.'
import { isValidCoordinate } from './helpers/coordinate'

export interface ICoordinates extends ITableColumn {
  Settings: null
}

export interface ICoordinatesCell extends ICell {
  settings: null
}

export class CoordinatesCell implements ICoordinatesCell {
  value: Cell
  settings: null

  constructor(value: Cell, settings: null) {
    // Initialize with valid coordinate or default { Lat: 0, Lon: 0 }
    this.value = this.parseCoordinates(value)
    this.settings = settings
  }

  toString(): string {
    let coordinates: ICoordinate = { Lat: 0, Lon: 0 }

    if (this.isCoordinate(this.value)) {
      coordinates = { Lat: this.value.Lat, Lon: this.value.Lon }
    }

    const lat = formatNumberCol(coordinates.Lat, {
      numPrecision: '0.00001',
      numThousandSeparator: 'none',
    })

    const lon = formatNumberCol(coordinates.Lon, {
      numPrecision: '0.00001',
      numThousandSeparator: 'none',
    })

    return `${lat}, ${lon}`
  }

  private parseCoordinates(value: Cell): ICoordinate {
    if (!value) return { Lat: 0, Lon: 0 }

    // Check if value is an object with Lat and Lon properties
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      if ('Lat' in value && 'Lon' in value) {
        const coordinates = { Lat: value.Lat, Lon: value.Lon }
        return this.isCoordinate(coordinates) ? coordinates : { Lat: 0, Lon: 0 }
      }
    }

    if (Array.isArray(value) && value.length === 2) {
      const [Lat, Lon] = value
      const coordinates = { Lat, Lon }
      return this.isCoordinate(coordinates) ? coordinates : { Lat: 0, Lon: 0 }
    }

    if (typeof value === 'string') {
      const [latStr, lonStr] = value.split(',').map((str) => str.trim())
      const Lat = parseFloat(latStr)
      const Lon = parseFloat(lonStr)

      const coordinates = { Lat, Lon }
      return this.isCoordinate(coordinates) ? coordinates : { Lat: 0, Lon: 0 }
    }

    return { Lat: 0, Lon: 0 }
  }

  toCell<T extends Cell>(newValue: T) {
    if (typeof newValue !== 'string' || typeof newValue === 'undefined' || !isValidCoordinate(newValue)) {
      // If the input is invalid, return the existing instance without changes
      return this
    }

    // Extract latitude and longitude from the valid input
    const [latStr, lonStr] = newValue.split(',').map((str) => str.trim())
    const Lat = parseFloat(latStr)
    const Lon = parseFloat(lonStr)

    // Set the new valid coordinate value
    this.value = { Lat, Lon } as ICoordinate
    return this
  }

  // Check if the value is a valid coordinate
  private isCoordinate(value: any): value is ICoordinate {
    return value && typeof value.Lat === 'number' && typeof value.Lon === 'number'
  }
}
